// Utility function to convert "naol" (case-insensitive) to "NAOL" in text
export const convertNaolToNAOL = (text) => {
  if (typeof text !== "string") return text;
  // Use case-insensitive replacement to handle naol, Naol, NAOL, etc.
  return text.replace(/\bnaol\b/gi, 'NAOL');
};

// Render text with bold formatting and clickable email/phone/URL links
export const renderWithBold = (text) => {
  if (typeof text !== "string") return text;
  
  // Convert "naol" to "NAOL" first
  text = convertNaolToNAOL(text);

  // Strip anything after "Note:" or "Please note:" (inclusive)
  let cutoffIndex = text.indexOf("Note:");
  const pleaseNoteIndex = text.indexOf("Please note:");
  if (pleaseNoteIndex !== -1 && (cutoffIndex === -1 || pleaseNoteIndex < cutoffIndex)) {
    cutoffIndex = pleaseNoteIndex;
  }
  const visibleText = cutoffIndex === -1 ? text : text.slice(0, cutoffIndex).trimEnd();

  // Simple markdown-style **bold** handling:
  // Split on '**' and alternate between normal and bold segments.
  const segments = visibleText.split("**");
  
  // Process each segment to detect and link email/phone
  const processSegment = (segment, isBold = false, segmentKey = '') => {
    if (!segment) return null;

    // Regex patterns for email, phone numbers, and URLs
    // Email: standard email pattern
    const emailRegex = /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g;
    // Phone: matches various formats like +91-1234567890, (123) 456-7890, 123-456-7890, etc.
    // Minimum 7 digits, maximum 15 digits (international format)
    const phoneRegex = /\b(\+?\d{1,4}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)?\d{3,4}[-.\s]?\d{3,4}[-.\s]?\d{3,9}\b/g;
    // URL: http/https links
    const urlRegex = /\bhttps?:\/\/[^\s]+/g;
    
    // Find all matches with their positions
    const matches = [];
    
    // Find email matches
    let emailMatch;
    const emailMatches = [];
    while ((emailMatch = emailRegex.exec(segment)) !== null) {
      emailMatches.push({
        type: 'email',
        text: emailMatch[0],
        index: emailMatch.index,
        length: emailMatch[0].length
      });
    }
    
    // Find phone matches
    let phoneMatch;
    const phoneMatches = [];
    while ((phoneMatch = phoneRegex.exec(segment)) !== null) {
      // Clean and validate phone number
      const phoneText = phoneMatch[0].replace(/[-.\s()]/g, '');
      // Check if it's a valid phone number (7-15 digits)
      if (phoneText.length >= 7 && phoneText.length <= 15 && /^\d+$/.test(phoneText)) {
        phoneMatches.push({
          type: 'phone',
          text: phoneMatch[0],
          index: phoneMatch.index,
          length: phoneMatch[0].length
        });
      }
    }
    // Find URL matches
    let urlMatch;
    const urlMatches = [];
    while ((urlMatch = urlRegex.exec(segment)) !== null) {
      urlMatches.push({
        type: 'url',
        text: urlMatch[0],
        index: urlMatch.index,
        length: urlMatch[0].length
      });
    }
    
    // Combine and sort matches by index
    matches.push(...emailMatches, ...phoneMatches, ...urlMatches);
    matches.sort((a, b) => a.index - b.index);
    
    // Remove overlapping matches (prefer email over phone if they overlap)
    const filteredMatches = [];
    let lastEnd = 0;
    for (const match of matches) {
      if (match.index >= lastEnd) {
        filteredMatches.push(match);
        lastEnd = match.index + match.length;
      }
    }
    
    // If no matches, return the segment as-is
    if (filteredMatches.length === 0) {
      return isBold ? <strong key={`${segmentKey}-text`}>{segment}</strong> : segment;
    }
    
    // Build array of text parts and links
    const parts = [];
    let lastIndex = 0;
    let partIndex = 0;
    
    filteredMatches.forEach((match, idx) => {
      // Add text before match
      if (match.index > lastIndex) {
        const beforeText = segment.substring(lastIndex, match.index);
        if (beforeText) {
          if (isBold) {
            parts.push(<strong key={`${segmentKey}-before-${partIndex++}`}>{beforeText}</strong>);
          } else {
            parts.push(beforeText);
          }
        }
      }
      
      // Add the link
      if (match.type === 'email') {
        parts.push(
          <a
            key={`${segmentKey}-email-${idx}`}
            href={`mailto:${match.text}`}
            className="text-[#0074bc] underline hover:text-[#1a6ba0] font-medium"
          >
            {isBold ? <strong>{match.text}</strong> : match.text}
          </a>
        );
      } else if (match.type === 'phone') {
        // Clean phone number for tel: link (remove spaces, dashes, parentheses)
        const cleanPhone = match.text.replace(/[-.\s()]/g, '');
        parts.push(
          <a
            key={`${segmentKey}-phone-${idx}`}
            href={`tel:${cleanPhone}`}
            className="text-[#0074bc] underline hover:text-[#1a6ba0] font-medium"
          >
            {isBold ? <strong>{match.text}</strong> : match.text}
          </a>
        );
      } else if (match.type === 'url') {
        parts.push(
          <a
            key={`${segmentKey}-url-${idx}`}
            href={match.text}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0074bc] underline hover:text-[#1a6ba0] font-medium break-all"
            style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
          >
            {isBold ? <strong>{match.text}</strong> : match.text}
          </a>
        );
      }
      
      lastIndex = match.index + match.length;
    });
    
    // Add remaining text after last match
    if (lastIndex < segment.length) {
      const afterText = segment.substring(lastIndex);
      if (afterText) {
        if (isBold) {
          parts.push(<strong key={`${segmentKey}-after`}>{afterText}</strong>);
        } else {
          parts.push(afterText);
        }
      }
    }
    
    return parts;
  };
  
  if (segments.length === 1) {
    const result = processSegment(visibleText, false, 'seg-0');
    return result;
  }

  const allParts = [];
  segments.forEach((segment, index) => {
    if (segment) {
      const isBold = index % 2 === 1;
      const segmentResult = processSegment(segment, isBold, `seg-${index}`);
      if (Array.isArray(segmentResult)) {
        allParts.push(...segmentResult);
      } else if (segmentResult) {
        allParts.push(segmentResult);
      }
    }
  });
  
  return allParts.length > 0 ? allParts : visibleText;
};

